import { useEffect, useRef } from "react";
import { useIsUnifiedMode } from "@/shared/utils";

export const useFixPosition = () => {
  const isUnifiedMode = useIsUnifiedMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const initialTopRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isUnifiedMode) {
      // Сбрасываем стили при выходе из unified режима
      if (containerRef.current) {
        containerRef.current.style.transform = "";
      }
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    // Запрашиваем начальные границы сразу
    try {
      window.parent.postMessage({ type: "get-viewport-bounds" }, "*");
    } catch (e) {
      // ignore
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "viewport-bounds" &&
        typeof event.data.top === "number"
      ) {
        if (initialTopRef.current === null) {
          // Запоминаем начальную позицию элемента относительно верха документа
          // В iframe window.scrollY всегда 0, поэтому rect.top - это и есть позиция от верха
          const rect = element.getBoundingClientRect();
          initialTopRef.current = rect.top;
        }

        const viewportTop = event.data.top;
        const initialTop = initialTopRef.current || 0;

        // Вычисляем смещение.
        // Элемент должен сдвигаться вниз, только если viewport ушел ниже его начала.
        // Добавляем небольшой отступ сверху (24px), чтобы не прилипал к краю.
        const topMargin = 24;
        let newOffset = viewportTop - initialTop + topMargin;

        // Ограничение сверху: не подниматься выше начальной позиции
        newOffset = Math.max(0, newOffset);

        // Ограничение снизу: не уезжать за пределы родительского контейнера
        const parent = element.parentElement;
        if (parent) {
          // Высота родителя минус высота элемента = максимальное смещение
          const maxOffset = parent.clientHeight - element.offsetHeight;
          // Применяем ограничение только если родитель выше элемента (есть место для скролла)
          if (maxOffset > 0) {
            newOffset = Math.min(newOffset, maxOffset);
          }
        }

        // Применяем стили напрямую для производительности (без ре-рендера React)
        element.style.transform = `translateY(${newOffset}px)`;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isUnifiedMode]);
  return { containerRef };
};

